import {
  cloneElement,
  isValidElement,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { EXPLAIN, type ExplainId } from '../content/explain';
import { placePopover, type ExplainSide, type Placement } from './explainPlacement';
import { useEffectiveReducedMotion } from './useReducedMotion';
import { EDGES } from '../theme/tokens';

// The one tooltip primitive. Wrap any surface; it explains itself on
// hover, focus, or touch.
//
// Replaces four unrelated mechanisms that were doing this job with
// different trigger models and no shared styling — see
// docs/tooltip-audit.md §1 for the inventory.
//
// Three things are load-bearing:
//
// 1. IT PORTALS. The tavern is `overflow-hidden` at essentially every
//    level (main, section, both columns, every rail). An absolutely
//    positioned panel inside the tree gets clipped by whichever ancestor
//    is nearest. Rendering to document.body escapes all of them, at the
//    cost of having to position by hand — see explainPlacement.ts.
//
// 2. TOUCH IS NOT AN AFTERTHOUGHT. Native `title` never fires on touch,
//    and this is a LatAm-first mobile title. Pointer type is read off the
//    event rather than sniffed from a media query, so a hybrid laptop
//    behaves correctly on both inputs within the same session.
//
// 3. TAP MUST NOT STEAL THE ACTION. On a surface where tapping already
//    does something (buy a card, reroll the shop), `touch="longpress"`
//    routes the explanation to a 500ms hold and leaves the tap alone.
//    This mirrors the split HandRail already makes between tap-to-read
//    and drag-to-play.

/** Above the hand rail (45), lifted sections (40) and modals (50).
    A tooltip is transient and only ever visible while its own trigger is
    being pointed at, so top-most is the correct answer — it can't occlude
    something the player is trying to reach. */
const Z_INDEX = 60;

const HOVER_OPEN_MS = 150;
const HOVER_CLOSE_MS = 80;
const LONG_PRESS_MS = 500;
/** A pointer that travels further than this during a hold is a scroll or
    a drag, not a long-press. */
const LONG_PRESS_SLOP_PX = 10;

export interface ExplainProps {
  /** Copy key. Omit only if passing `title`/`body` directly. */
  id?: ExplainId;
  /** Overrides `id`'s title. */
  title?: string;
  /** Overrides `id`'s body. */
  body?: string;
  /** Live, call-site-specific line under the body — cost, current state,
      whether it's affordable. Kept out of the copy module because it
      changes every render. */
  detail?: string;
  /** Preferred side; flips automatically when it doesn't fit. */
  side?: ExplainSide;
  /**
   * How touch opens the popover.
   *   'tap'       — default. For surfaces that aren't themselves actions
   *                 (HUD chips, labels, counters).
   *   'longpress' — for surfaces where tap already does something.
   */
  touch?: 'tap' | 'longpress';
  /** Applied to the wrapper. Needed wherever the wrapper has to
      participate in the parent's layout (`flex w-full` in the rail). */
  className?: string;
  /** Turns the trigger off without unmounting — used where the target
      only sometimes has something to say. */
  disabled?: boolean;
  children: React.ReactNode;
}

export function Explain({
  id,
  title,
  body,
  detail,
  side = 'top',
  touch = 'tap',
  className = '',
  disabled = false,
  children,
}: ExplainProps) {
  const copy = id ? EXPLAIN[id] : undefined;
  const resolvedTitle = title ?? copy?.title;
  const resolvedBody = body ?? copy?.body;

  const reducedMotion = useEffectiveReducedMotion();
  const panelId = useId();

  const triggerRef = useRef<HTMLSpanElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const openTimer = useRef<number | null>(null);
  const closeTimer = useRef<number | null>(null);
  const pressTimer = useRef<number | null>(null);
  const pressOrigin = useRef<{ x: number; y: number } | null>(null);
  /** Set when a long press opens the popover, so the click that lands on
      release can be swallowed before it reaches the button underneath. */
  const swallowNextClick = useRef(false);

  const [open, setOpen] = useState(false);
  const [placement, setPlacement] = useState<Placement | null>(null);

  const inert = disabled || !resolvedBody;

  const clearTimers = useCallback(() => {
    for (const t of [openTimer, closeTimer, pressTimer]) {
      if (t.current !== null) {
        window.clearTimeout(t.current);
        t.current = null;
      }
    }
  }, []);

  const close = useCallback(() => {
    clearTimers();
    setOpen(false);
    setPlacement(null);
  }, [clearTimers]);

  const openNow = useCallback(() => {
    clearTimers();
    if (inert) return;
    setOpen(true);
  }, [clearTimers, inert]);

  // Measure and place. useLayoutEffect so the panel is positioned before
  // paint — placing in a passive effect shows one frame at the wrong
  // coordinates, which reads as a flicker at the top-left of the screen.
  useLayoutEffect(() => {
    if (!open) return;
    const trigger = triggerRef.current;
    const panel = panelRef.current;
    if (!trigger || !panel) return;
    const a = trigger.getBoundingClientRect();
    const p = panel.getBoundingClientRect();
    setPlacement(
      placePopover({
        anchor: { x: a.x, y: a.y, width: a.width, height: a.height },
        panel: { width: p.width, height: p.height },
        viewport: { width: window.innerWidth, height: window.innerHeight },
        side,
      }),
    );
  }, [open, side, resolvedTitle, resolvedBody, detail]);

  // Dismissal. Coordinates are captured at open time, so anything that
  // moves the trigger invalidates them — close rather than chase.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    const onOutside = (e: PointerEvent) => {
      if (!triggerRef.current?.contains(e.target as Node)) close();
    };
    window.addEventListener('keydown', onKey);
    // Capture phase: a tap on a button elsewhere should dismiss this
    // before that button's own handler runs and re-lays-out the page.
    window.addEventListener('pointerdown', onOutside, true);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('pointerdown', onOutside, true);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open, close]);

  useEffect(() => clearTimers, [clearTimers]);

  // --- Pointer handling ---------------------------------------------
  // Mouse gets hover with a short intent delay. Touch and pen get either
  // tap-to-toggle or press-and-hold, per `touch`.

  function handlePointerEnter(e: React.PointerEvent) {
    if (inert || e.pointerType !== 'mouse') return;
    clearTimers();
    openTimer.current = window.setTimeout(openNow, HOVER_OPEN_MS);
  }

  function handlePointerLeave(e: React.PointerEvent) {
    if (e.pointerType !== 'mouse') return;
    clearTimers();
    closeTimer.current = window.setTimeout(close, HOVER_CLOSE_MS);
  }

  function handlePointerDown(e: React.PointerEvent) {
    // Clear any stale swallow flag: a long press that opened the popover
    // but never produced a click (finger slid off the button before
    // release) would otherwise eat the next genuine tap.
    swallowNextClick.current = false;
    if (inert || e.pointerType === 'mouse') return;
    if (touch === 'tap') {
      // Toggle, so a second tap dismisses without waiting for the
      // outside-tap listener.
      if (open) close();
      else openNow();
      return;
    }
    pressOrigin.current = { x: e.clientX, y: e.clientY };
    clearTimers();
    pressTimer.current = window.setTimeout(() => {
      // Reading a button must never also press it. Without this, holding
      // Reroll to find out what it does would open the popover AND spend
      // the gold the moment the finger lifted.
      swallowNextClick.current = true;
      openNow();
    }, LONG_PRESS_MS);
  }

  function handlePointerMove(e: React.PointerEvent) {
    const origin = pressOrigin.current;
    if (!origin || pressTimer.current === null) return;
    const moved = Math.hypot(e.clientX - origin.x, e.clientY - origin.y);
    if (moved > LONG_PRESS_SLOP_PX) {
      window.clearTimeout(pressTimer.current);
      pressTimer.current = null;
    }
  }

  function handlePointerUp() {
    pressOrigin.current = null;
    if (pressTimer.current !== null) {
      window.clearTimeout(pressTimer.current);
      pressTimer.current = null;
    }
  }

  // Capture phase, so this runs before the wrapped button's own onClick.
  function handleClickCapture(e: React.MouseEvent) {
    if (!swallowNextClick.current) return;
    swallowNextClick.current = false;
    e.preventDefault();
    e.stopPropagation();
  }

  // Keyboard parity: focus opens immediately (no intent delay — arriving
  // by Tab is already deliberate).
  function handleFocus() {
    if (inert) return;
    openNow();
  }

  // Put aria-describedby on the actual focusable child where possible, so
  // a screen reader announces the description with the control rather
  // than with an anonymous wrapper.
  //
  // Only host elements (a real <button>, <span>, …) accept it — cloning it
  // onto a component child just drops it, since the component decides what
  // to spread onto the DOM. In that case fall back to the wrapper, which
  // at least keeps the association in the accessibility tree.
  const childIsHostElement =
    isValidElement(children) && typeof children.type === 'string';
  const describe = open && !!resolvedBody;
  const described =
    describe && childIsHostElement
      ? cloneElement(children as React.ReactElement<{ 'aria-describedby'?: string }>, {
          'aria-describedby': panelId,
        })
      : children;

  return (
    <>
      <span
        ref={triggerRef}
        aria-describedby={describe && !childIsHostElement ? panelId : undefined}
        className={className}
        onPointerEnter={handlePointerEnter}
        onPointerLeave={handlePointerLeave}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onClickCapture={handleClickCapture}
        onFocus={handleFocus}
        onBlur={close}
      >
        {described}
      </span>

      {typeof document !== 'undefined' &&
        createPortal(
          <AnimatePresence>
            {open && resolvedBody && (
              <motion.div
                key={panelId}
                ref={panelRef}
                id={panelId}
                role="tooltip"
                initial={reducedMotion ? { opacity: 0 } : { opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={reducedMotion ? { opacity: 0 } : { opacity: 0, scale: 0.98 }}
                transition={{ duration: reducedMotion ? 0 : 0.12, ease: 'easeOut' }}
                className="pointer-events-none fixed w-[min(280px,calc(100vw-16px))] rounded-md border bg-stucco1 px-sm py-xs shadow-[0_8px_24px_rgba(0,0,0,0.55)] md:px-md md:py-sm"
                style={{
                  zIndex: Z_INDEX,
                  borderColor: EDGES.primedSoft,
                  // Until measured, park it off-screen rather than at 0,0 —
                  // the layout effect fills this in before paint, but a
                  // portal that mounts mid-scroll can render once first.
                  left: placement?.left ?? -9999,
                  top: placement?.top ?? -9999,
                }}
              >
                {resolvedTitle && (
                  <p className="font-data text-[9px] uppercase tracking-[0.25em] text-sodium md:text-[10px]">
                    {resolvedTitle}
                  </p>
                )}
                <p className="mt-[3px] text-[11px] leading-snug text-bone md:text-[12px]">
                  {resolvedBody}
                </p>
                {detail && (
                  <p className="mt-xs border-t pt-xs font-data text-[9px] uppercase tracking-[0.2em] text-graphite md:text-[10px]"
                     style={{ borderColor: EDGES.soft }}>
                    {detail}
                  </p>
                )}
              </motion.div>
            )}
          </AnimatePresence>,
          document.body,
        )}
    </>
  );
}
